import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { api } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/data-table'
import { StatusBadge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { useState } from 'react'
import { Building2, RefreshCw, Plus, ShieldCheck, ShieldOff, ShieldAlert } from 'lucide-react'

interface Tenant {
  id: string; name: string; slug: string; plan: string; status: string
  max_m365_tenants: number; max_users: number; created_at: string
}

interface M365Tenant {
  id: string; azure_tenant_id: string; display_name: string; domain: string | null
  status: string; last_sync_at: string | null; last_sync_status: string | null
  last_error: string | null; sync_enabled: number; has_credentials: number; created_at: string
}

interface TenantUser {
  id: string; email: string; name: string; role: string; status: string
  last_login_at: string | null; created_at: string
  mfa_enabled: number; mfa_enforced: number
}

export function AdminClientDetailPage() {
  const { clientId } = useParams({ strict: false }) as { clientId: string }
  const queryClient = useQueryClient()

  // ── Tenant info ──
  const { data: tenant } = useQuery({
    queryKey: ['admin', 'tenants', clientId],
    queryFn: () => api.get<Tenant>(`/admin/tenants/${clientId}`),
  })

  // ── M365 tenants ──
  const { data: m365Tenants } = useQuery({
    queryKey: ['admin', 'tenants', clientId, 'm365'],
    queryFn: () => api.get<M365Tenant[]>(`/admin/tenants/${clientId}/m365`),
  })

  const syncM365Mutation = useMutation({
    mutationFn: (m365Id: string) => api.post(`/admin/tenants/${clientId}/m365/${m365Id}/sync`),
    onSuccess: () => {
      toast.success('Sincronizacion iniciada')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'm365'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  // ── Manual M365 connect ──
  const [showManualConnect, setShowManualConnect] = useState(false)
  const [m365Form, setM365Form] = useState({ azureTenantId: '', displayName: '', domain: '', clientId: '', clientSecret: '' })

  const manualConnectMutation = useMutation({
    mutationFn: () => api.post(`/admin/tenants/${clientId}/m365-manual`, {
      azureTenantId: m365Form.azureTenantId,
      displayName: m365Form.displayName,
      domain: m365Form.domain || undefined,
      clientId: m365Form.clientId || undefined,
      clientSecret: m365Form.clientSecret || undefined,
    }),
    onSuccess: () => {
      toast.success('Tenant M365 conectado y sincronizacion iniciada')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'm365'] })
      setShowManualConnect(false)
      setM365Form({ azureTenantId: '', displayName: '', domain: '', clientId: '', clientSecret: '' })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const mf = (field: string, val: string) => setM365Form((f) => ({ ...f, [field]: val }))

  // ── Edit M365 tenant ──
  const [editingM365, setEditingM365] = useState<string | null>(null)
  const [editM365Form, setEditM365Form] = useState({
    displayName: '', domain: '', azureTenantId: '', syncEnabled: true,
    clientId: '', clientSecret: '', clearCredentials: false,
  })

  const startEditM365 = (m: M365Tenant) => {
    setEditingM365(m.id)
    setEditM365Form({
      displayName: m.display_name, domain: m.domain ?? '', azureTenantId: m.azure_tenant_id,
      syncEnabled: !!m.sync_enabled, clientId: '', clientSecret: '', clearCredentials: false,
    })
  }

  const saveM365Mutation = useMutation({
    mutationFn: (m365Id: string) => api.put(`/admin/tenants/${clientId}/m365/${m365Id}`, {
      displayName: editM365Form.displayName,
      domain: editM365Form.domain || undefined,
      azureTenantId: editM365Form.azureTenantId,
      syncEnabled: editM365Form.syncEnabled,
      clientId: editM365Form.clientId || undefined,
      clientSecret: editM365Form.clientSecret || undefined,
      clearCredentials: editM365Form.clearCredentials,
    }),
    onSuccess: () => {
      toast.success('Tenant M365 actualizado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'm365'] })
      setEditingM365(null)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const deleteM365Mutation = useMutation({
    mutationFn: (m365Id: string) => api.delete(`/admin/tenants/${clientId}/m365/${m365Id}`),
    onSuccess: () => {
      toast.success('Tenant M365 desconectado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'm365'] })
    },
  })

  const em = (field: string, val: string | boolean) => setEditM365Form((f) => ({ ...f, [field]: val }))

  // ── Edit tenant ──
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({ name: '', plan: '', status: '', maxM365Tenants: '', maxUsers: '' })

  const startEdit = () => {
    if (!tenant) return
    setEditForm({
      name: tenant.name, plan: tenant.plan, status: tenant.status,
      maxM365Tenants: String(tenant.max_m365_tenants), maxUsers: String(tenant.max_users),
    })
    setEditing(true)
  }

  const saveTenantMutation = useMutation({
    mutationFn: () => api.put(`/admin/tenants/${clientId}`, {
      name: editForm.name, plan: editForm.plan, status: editForm.status,
      maxM365Tenants: parseInt(editForm.maxM365Tenants), maxUsers: parseInt(editForm.maxUsers),
    }),
    onSuccess: () => {
      toast.success('Cliente actualizado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      setEditing(false)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  // ── Users ──
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'tenants', clientId, 'users'],
    queryFn: () => api.get<TenantUser[]>(`/admin/tenants/${clientId}/users`),
  })

  const [showAddUser, setShowAddUser] = useState(false)
  const [userForm, setUserForm] = useState({ email: '', name: '', password: '', role: 'tenant_viewer' })

  const addUserMutation = useMutation({
    mutationFn: () => api.post(`/admin/tenants/${clientId}/users`, userForm),
    onSuccess: () => {
      toast.success('Usuario creado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'users'] })
      setShowAddUser(false)
      setUserForm({ email: '', name: '', password: '', role: 'tenant_viewer' })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [editUserForm, setEditUserForm] = useState({ name: '', role: '', status: '', password: '' })

  const startEditUser = (u: TenantUser) => {
    setEditingUser(u.id)
    setEditUserForm({ name: u.name, role: u.role, status: u.status, password: '' })
  }

  const saveUserMutation = useMutation({
    mutationFn: (userId: string) => {
      const body: Record<string, string> = {}
      if (editUserForm.name) body.name = editUserForm.name
      if (editUserForm.role) body.role = editUserForm.role
      if (editUserForm.status) body.status = editUserForm.status
      if (editUserForm.password) body.password = editUserForm.password
      return api.put(`/admin/tenants/${clientId}/users/${userId}`, body)
    },
    onSuccess: () => {
      toast.success('Usuario actualizado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'users'] })
      setEditingUser(null)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/admin/tenants/${clientId}/users/${userId}`),
    onSuccess: () => {
      toast.success('Usuario eliminado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'users'] })
    },
  })

  const resetMfaMutation = useMutation({
    mutationFn: (userId: string) =>
      api.put(`/admin/tenants/${clientId}/users/${userId}`, { resetMfa: true }),
    onSuccess: () => {
      toast.success('MFA reseteado. El usuario debera reconfigurar TOTP en su proximo login.')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'users'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const enforcementMutation = useMutation({
    mutationFn: ({ userId, enforced }: { userId: string; enforced: boolean }) =>
      api.put(`/admin/tenants/${clientId}/users/${userId}/mfa-enforcement`, { enforced }),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.enforced
          ? 'MFA marcado como obligatorio. El usuario debera configurarlo en su proximo login.'
          : 'Usuario eximido de MFA. Podra acceder sin TOTP.',
      )
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'users'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const uf = (field: string, val: string) => setUserForm((f) => ({ ...f, [field]: val }))
  const ef = (field: string, val: string) => setEditForm((f) => ({ ...f, [field]: val }))
  const euf = (field: string, val: string) => setEditUserForm((f) => ({ ...f, [field]: val }))

  return (
    <div className="space-y-6">
      <PageHeader
        title={tenant?.name ?? 'Cargando...'}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => window.location.href = '/admin/clients'}>Volver</Button>
            <Button variant="secondary" onClick={() => window.location.href = `/admin/clients/${clientId}/recommendations`}>
              Recomendaciones
            </Button>
            <Button variant="secondary" onClick={() => window.location.href = `/admin/clients/${clientId}/licenses`}>
              Licencias
            </Button>
            {!editing && <Button onClick={startEdit}>Editar cliente</Button>}
          </div>
        }
      />

      {/* Edit tenant */}
      {editing && (
        <Card>
          <h3 className="mb-4 font-semibold" style={{ color: 'var(--color-text-primary)' }}>Editar cliente</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Nombre</label>
              <input type="text" value={editForm.name} onChange={(e) => ef('name', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Plan</label>
              <select value={editForm.plan} onChange={(e) => ef('plan', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                <option value="trial">Trial</option><option value="starter">Starter</option>
                <option value="pro">Pro</option><option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Estado</label>
              <select value={editForm.status} onChange={(e) => ef('status', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                <option value="active">Activo</option><option value="suspended">Suspendido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Max tenants M365</label>
              <input type="number" value={editForm.maxM365Tenants} onChange={(e) => ef('maxM365Tenants', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Max usuarios</label>
              <input type="number" value={editForm.maxUsers} onChange={(e) => ef('maxUsers', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => saveTenantMutation.mutate()} loading={saveTenantMutation.isPending}>Guardar</Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>Cancelar</Button>
          </div>
        </Card>
      )}

      {/* Tenant info summary */}
      {!editing && tenant && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {[
            { label: 'Plan', value: <StatusBadge status={tenant.plan} /> },
            { label: 'Estado', value: <StatusBadge status={tenant.status} /> },
            { label: 'Max M365', value: tenant.max_m365_tenants },
            { label: 'Max Usuarios', value: tenant.max_users },
            { label: 'Usuarios actuales', value: users?.length ?? 0 },
          ].map((item, i) => (
            <Card key={i}>
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{item.label}</p>
              <p className="mt-1 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{item.value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* M365 Tenants section */}
      <Card>
        <CardHeader>
          <CardTitle>Tenants Microsoft 365</CardTitle>
          <Button size="sm" onClick={() => setShowManualConnect(!showManualConnect)}>
            <Plus className="h-4 w-4" /> {showManualConnect ? 'Cancelar' : 'Registro manual'}
          </Button>
        </CardHeader>

        {/* Manual connect form */}
        {showManualConnect && (
          <div className="mb-4 rounded-lg border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
            <p className="mb-3 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Registro manual de tenant M365
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  Azure Tenant ID *
                </label>
                <input type="text" value={m365Form.azureTenantId} onChange={(e) => mf('azureTenantId', e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full rounded-md border px-3 py-2 text-sm font-mono" style={{ borderColor: 'var(--color-border)' }} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  Nombre del tenant *
                </label>
                <input type="text" value={m365Form.displayName} onChange={(e) => mf('displayName', e.target.value)}
                  placeholder="Nombre de la organizacion"
                  className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  Dominio principal
                </label>
                <input type="text" value={m365Form.domain} onChange={(e) => mf('domain', e.target.value)}
                  placeholder="empresa.onmicrosoft.com"
                  className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              </div>
              <div className="md:col-span-2">
                <div className="mb-2 mt-1 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                  <p className="text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                    Credenciales de la App de Azure AD
                  </p>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  Client ID *
                </label>
                <input type="text" value={m365Form.clientId} onChange={(e) => mf('clientId', e.target.value)}
                  placeholder="Client ID de Azure AD"
                  className="w-full rounded-md border px-3 py-2 text-sm font-mono" style={{ borderColor: 'var(--color-border)' }} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  Client Secret *
                </label>
                <input type="password" value={m365Form.clientSecret} onChange={(e) => mf('clientSecret', e.target.value)}
                  placeholder="Client Secret de Azure AD"
                  className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => manualConnectMutation.mutate()} loading={manualConnectMutation.isPending}
                disabled={!m365Form.azureTenantId || !m365Form.displayName || !m365Form.clientId || !m365Form.clientSecret}>
                Registrar y sincronizar
              </Button>
              <Button variant="secondary" onClick={() => setShowManualConnect(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {!showManualConnect && !m365Tenants?.length ? (
          <div className="flex flex-col items-center py-8 text-center">
            <Building2 className="mb-3 h-10 w-10" style={{ color: 'var(--color-text-tertiary)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>Sin tenants M365 conectados</p>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              Registra manualmente el tenant de Microsoft 365
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(m365Tenants ?? []).map((m) => (
              <div key={m.id} className="rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
                {editingM365 === m.id ? (
                  /* ── Edit mode ── */
                  <div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Nombre *</label>
                        <input type="text" value={editM365Form.displayName} onChange={(e) => em('displayName', e.target.value)}
                          className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Azure Tenant ID *</label>
                        <input type="text" value={editM365Form.azureTenantId} onChange={(e) => em('azureTenantId', e.target.value)}
                          className="w-full rounded-md border px-3 py-2 text-sm font-mono" style={{ borderColor: 'var(--color-border)' }} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Dominio</label>
                        <input type="text" value={editM365Form.domain} onChange={(e) => em('domain', e.target.value)}
                          className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
                      </div>
                      <div className="flex items-end gap-3 pb-1">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={editM365Form.syncEnabled}
                            onChange={(e) => em('syncEnabled', e.target.checked)} />
                          Sync automatica habilitada
                        </label>
                      </div>
                      <div className="md:col-span-2 border-t pt-3 mt-1" style={{ borderColor: 'var(--color-border)' }}>
                        <p className="mb-2 text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                          Credenciales de la App de Azure AD {m.has_credentials ? '(configuradas)' : '(sin configurar)'}
                        </p>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Client ID</label>
                        <input type="text" value={editM365Form.clientId} onChange={(e) => em('clientId', e.target.value)}
                          placeholder="Dejar vacio para no cambiar"
                          className="w-full rounded-md border px-3 py-2 text-sm font-mono" style={{ borderColor: 'var(--color-border)' }} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Client Secret</label>
                        <input type="password" value={editM365Form.clientSecret} onChange={(e) => em('clientSecret', e.target.value)}
                          placeholder="Dejar vacio para no cambiar"
                          className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
                      </div>
                      {m.has_credentials ? (
                        <div className="md:col-span-2">
                          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-warning)' }}>
                            <input type="checkbox" checked={editM365Form.clearCredentials}
                              onChange={(e) => em('clearCredentials', e.target.checked)} />
                            Eliminar credenciales
                          </label>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button onClick={() => saveM365Mutation.mutate(m.id)} loading={saveM365Mutation.isPending}>Guardar</Button>
                      <Button variant="secondary" onClick={() => setEditingM365(null)}>Cancelar</Button>
                      <Button variant="danger" size="sm" className="ml-auto"
                        onClick={() => { if (confirm('Desconectar este tenant M365?')) deleteM365Mutation.mutate(m.id) }}>
                        Desconectar
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ── View mode ── */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: 'var(--color-brand-primary-50)' }}>
                        <Building2 className="h-5 w-5" style={{ color: 'var(--color-brand-primary)' }} />
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{m.display_name}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                          {m.domain ?? m.azure_tenant_id}
                          {m.has_credentials ? '' : ' — Sin credenciales'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right text-sm">
                        <StatusBadge status={m.status} />
                        {m.last_sync_at && (
                          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                            Sync: {formatDateTime(m.last_sync_at)}
                            {m.last_sync_status && <> — <StatusBadge status={m.last_sync_status} /></>}
                          </p>
                        )}
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => startEditM365(m)}>Editar</Button>
                      <Button variant="secondary" size="sm" onClick={() => syncM365Mutation.mutate(m.id)}
                        loading={syncM365Mutation.isPending}>
                        <RefreshCw className="h-3.5 w-3.5" /> Sync
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Users section */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios del cliente</CardTitle>
          <Button size="sm" onClick={() => setShowAddUser(!showAddUser)}>
            {showAddUser ? 'Cancelar' : 'Nuevo usuario'}
          </Button>
        </CardHeader>

        {showAddUser && (
          <div className="mb-4 rounded-lg border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <input type="text" placeholder="Nombre *" value={userForm.name}
                onChange={(e) => uf('name', e.target.value)}
                className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              <input type="email" placeholder="Email *" value={userForm.email}
                onChange={(e) => uf('email', e.target.value)}
                className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              <input type="password" placeholder="Contrasena *" value={userForm.password}
                onChange={(e) => uf('password', e.target.value)}
                className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              <div className="flex gap-2">
                <select value={userForm.role} onChange={(e) => uf('role', e.target.value)}
                  className="flex-1 rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                  <option value="tenant_viewer">Visor</option>
                  <option value="tenant_admin">Admin</option>
                </select>
                <Button size="sm" onClick={() => addUserMutation.mutate()} loading={addUserMutation.isPending}>
                  Crear
                </Button>
              </div>
            </div>
          </div>
        )}

        <DataTable
          columns={[
            {
              key: 'name', header: 'Nombre',
              render: (r) => {
                const row = r as unknown as TenantUser
                if (editingUser === row.id) {
                  return (
                    <input type="text" value={editUserForm.name} onChange={(e) => euf('name', e.target.value)}
                      className="w-full rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--color-border)' }} />
                  )
                }
                return row.name
              },
            },
            { key: 'email', header: 'Email' },
            {
              key: 'role', header: 'Rol',
              render: (r) => {
                const row = r as unknown as TenantUser
                if (editingUser === row.id) {
                  return (
                    <select value={editUserForm.role} onChange={(e) => euf('role', e.target.value)}
                      className="rounded border px-2 py-1 text-xs" style={{ borderColor: 'var(--color-border)' }}>
                      <option value="tenant_admin">Admin</option>
                      <option value="tenant_viewer">Visor</option>
                    </select>
                  )
                }
                return row.role === 'tenant_admin' ? 'Admin' : 'Visor'
              },
            },
            {
              key: 'status', header: 'Estado',
              render: (r) => {
                const row = r as unknown as TenantUser
                if (editingUser === row.id) {
                  return (
                    <select value={editUserForm.status} onChange={(e) => euf('status', e.target.value)}
                      className="rounded border px-2 py-1 text-xs" style={{ borderColor: 'var(--color-border)' }}>
                      <option value="active">Activo</option>
                      <option value="disabled">Deshabilitado</option>
                    </select>
                  )
                }
                return <StatusBadge status={row.status} />
              },
            },
            {
              key: 'mfa', header: 'MFA',
              render: (r) => {
                const row = r as unknown as TenantUser
                if (row.mfa_enabled) {
                  return (
                    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
                      style={{ backgroundColor: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>
                      <ShieldCheck className="h-3.5 w-3.5" /> Activo
                    </span>
                  )
                }
                if (row.mfa_enforced) {
                  return (
                    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
                      style={{ backgroundColor: 'var(--color-warning-light)', color: 'var(--color-warning)' }}>
                      <ShieldAlert className="h-3.5 w-3.5" /> Pendiente
                    </span>
                  )
                }
                return (
                  <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
                    style={{ backgroundColor: 'var(--color-gray-100)', color: 'var(--color-text-tertiary)' }}>
                    <ShieldOff className="h-3.5 w-3.5" /> Exento
                  </span>
                )
              },
            },
            {
              key: 'last_login_at', header: 'Ultimo acceso',
              render: (r) => {
                const d = (r as Record<string, string | null>).last_login_at
                return d ? formatDateTime(d) : 'Nunca'
              },
            },
            {
              key: 'actions', header: '',
              render: (r) => {
                const row = r as unknown as TenantUser
                if (editingUser === row.id) {
                  return (
                    <div className="flex gap-1">
                      <Button variant="primary" size="sm" onClick={() => saveUserMutation.mutate(row.id)}
                        loading={saveUserMutation.isPending}>Guardar</Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>Cancelar</Button>
                    </div>
                  )
                }
                return (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEditUser(row)}>Editar</Button>
                    {row.mfa_enabled ? (
                      <Button variant="ghost" size="sm" title="Resetear MFA"
                        onClick={() => {
                          if (confirm(`Resetear MFA de ${row.name}? El usuario perdera el acceso TOTP y debera configurarlo de nuevo.`))
                            resetMfaMutation.mutate(row.id)
                        }}>
                        <ShieldAlert className="h-3.5 w-3.5" style={{ color: 'var(--color-warning)' }} />
                      </Button>
                    ) : null}
                    {row.mfa_enforced ? (
                      <Button variant="ghost" size="sm" title="Eximir de MFA"
                        onClick={() => {
                          if (confirm(
                            `Eximir a ${row.name} de MFA?\n\nEl usuario podra acceder sin TOTP. Si tenia MFA configurado, se borrara.\n\nEsta accion queda registrada en auditoria.`
                          ))
                            enforcementMutation.mutate({ userId: row.id, enforced: false })
                        }}>
                        <ShieldOff className="h-3.5 w-3.5" style={{ color: 'var(--color-text-tertiary)' }} />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" title="Re-exigir MFA"
                        onClick={() => {
                          if (confirm(
                            `Volver a exigir MFA a ${row.name}?\n\nEn su proximo login sera obligado a configurar TOTP.`
                          ))
                            enforcementMutation.mutate({ userId: row.id, enforced: true })
                        }}>
                        <ShieldCheck className="h-3.5 w-3.5" style={{ color: 'var(--color-success)' }} />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm"
                      style={{ color: 'var(--color-error)' }}
                      onClick={() => { if (confirm('Eliminar usuario?')) deleteUserMutation.mutate(row.id) }}>
                      Eliminar
                    </Button>
                  </div>
                )
              },
            },
          ]}
          data={(users ?? []) as unknown as Record<string, unknown>[]}
          loading={usersLoading}
        />
      </Card>
    </div>
  )
}
